import { useTellescopeMutation, type ParamsForMethod } from "@tellescope/tanstack-query";
import { htmlToText } from "@tellescope/ui";
import { useCallback } from "react";

export type DraftCreateFields =
  | ({ channel: "SMS" } & Omit<ParamsForMethod<"sms_messages", "createOne">, "message">)
  | ({ channel: "Chat" } & Omit<ParamsForMethod<"chats", "createOne">, "message">)
  | ({ channel: "Email" } & Omit<ParamsForMethod<"emails", "createOne">, "HTMLContent" | "textContent">);

export type DraftChannel = DraftCreateFields["channel"];

export interface SaveDraftArgs {
  message: string;
  fields: DraftCreateFields;
  id?: string;
}

export interface SendDraftArgs {
  id: string;
  message: string;
  fields: DraftCreateFields;
}

const DRAFT_SEND_AT_YEARS_AHEAD = 100;

const buildDraftSendAt = () => {
  const sendAt = new Date();
  sendAt.setFullYear(sendAt.getFullYear() + DRAFT_SEND_AT_YEARS_AHEAD);
  return sendAt;
};

export const useDraftMutations = () => {
  const createSmsMutation = useTellescopeMutation({ model: "sms_messages", method: "createOne" });
  const updateSmsMutation = useTellescopeMutation({ model: "sms_messages", method: "updateOne" });
  const deleteSmsMutation = useTellescopeMutation({ model: "sms_messages", method: "deleteOne" });
  const createChatMutation = useTellescopeMutation({ model: "chats", method: "createOne" });
  const updateChatMutation = useTellescopeMutation({ model: "chats", method: "updateOne" });
  const deleteChatMutation = useTellescopeMutation({ model: "chats", method: "deleteOne" });
  const createEmailMutation = useTellescopeMutation({ model: "emails", method: "createOne" });
  const deleteEmailMutation = useTellescopeMutation({ model: "emails", method: "deleteOne" });

  const saveDraft = useCallback(
    async ({ message, fields, id }: SaveDraftArgs): Promise<string | undefined> => {
      const draftSendAt = buildDraftSendAt();
      switch (fields.channel) {
        case "SMS": {
          const { channel: _channel, ...createFields } = fields;
          if (id) {
            const updated = await updateSmsMutation.mutateAsync({ id, message, isDraft: true, sendAt: draftSendAt });
            return updated?.id ?? id;
          }
          const created = await createSmsMutation.mutateAsync({
            ...createFields,
            message,
            isDraft: true,
            sendAt: draftSendAt,
          });
          return created?.id;
        }
        case "Chat": {
          const { channel: _channel, ...createFields } = fields;
          if (id) {
            const updated = await updateChatMutation.mutateAsync({ id, message, isDraft: true, sendAt: draftSendAt });
            return updated?.id ?? id;
          }
          const created = await createChatMutation.mutateAsync({
            ...createFields,
            message,
            isDraft: true,
            sendAt: draftSendAt,
          });
          return created?.id;
        }
        case "Email": {
          const { channel: _channel, ...createFields } = fields;
          if (id) await deleteEmailMutation.mutateAsync(id);
          const created = await createEmailMutation.mutateAsync({
            ...createFields,
            HTMLContent: message,
            textContent: htmlToText(message),
            isDraft: true,
            sendAt: draftSendAt,
          });
          return created?.id;
        }
      }
    },
    [
      createChatMutation,
      createEmailMutation,
      createSmsMutation,
      deleteEmailMutation,
      updateChatMutation,
      updateSmsMutation,
    ]
  );

  const sendDraft = useCallback(
    async ({ id, message, fields }: SendDraftArgs): Promise<string | undefined> => {
      switch (fields.channel) {
        case "SMS": {
          const sent = await updateSmsMutation.mutateAsync({ id, message, isDraft: false, sendAt: new Date() });
          return sent?.id ?? id;
        }
        case "Chat": {
          const { channel: _channel, roomId: _roomId, ...updatableFields } = fields;
          const sent = await updateChatMutation.mutateAsync({
            ...updatableFields,
            id,
            message,
            isDraft: false,
            sendAt: new Date(),
            timestamp: new Date(),
          });
          return sent?.id ?? id;
        }
        case "Email": {
          const { channel: _channel, ...createFields } = fields;
          await deleteEmailMutation.mutateAsync(id);
          const sent = await createEmailMutation.mutateAsync({
            ...createFields,
            HTMLContent: message,
            textContent: htmlToText(message),
          });
          return sent?.id;
        }
      }
    },
    [createEmailMutation, deleteEmailMutation, updateChatMutation, updateSmsMutation]
  );

  const deleteDraft = useCallback(
    async ({ id, channel }: { id: string; channel: DraftChannel }): Promise<void> => {
      switch (channel) {
        case "SMS":
          await deleteSmsMutation.mutateAsync(id);
          return;
        case "Chat":
          await deleteChatMutation.mutateAsync(id);
          return;
        case "Email":
          await deleteEmailMutation.mutateAsync(id);
          return;
      }
    },
    [deleteChatMutation, deleteEmailMutation, deleteSmsMutation]
  );

  return { saveDraft, sendDraft, deleteDraft };
};
